import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useBrandingContext } from "context/BrandingContext";
import { ThemeColors } from "utils/themeContextHelper";
import LoadingWithoutModal from "components/common/LoadingWithoutModal";
import { Inline } from "components/layout/reusableLayouts";
import { BRAND_KEYS, Celebration, DEFAULT_BRAND, toast } from "@outpave/ds";
import {
  cardFills,
  isCardFace,
  platformDesign,
} from "components/common/cards/CompanyPaymentCard";
import type { CardFace } from "components/common/cards/CompanyPaymentCard";
import BrandingPage from "components/common/branding/BrandingPage";
import CardDesignEditor from "components/common/branding/CardDesignEditor";
import {
  cardResetPatch,
  effectiveCard,
} from "components/common/branding/cardDesign";
import ResetBrandingButton from "components/common/branding/ResetBrandingButton";
import { useDraftTokens } from "components/common/branding/useDraftTokens";
import { useNavBreadcrumbs } from "hooks/useNavBreadcrumbs";
import {
  BRAND_CARD_KEYS,
  BRAND_CHART_KEYS,
  BRAND_COLOR_KEYS,
  chartChoiceOf,
  chartKeysOf,
} from "components/common/branding/BrandColorsPanel";
import { CompanyLogoSubparentType } from "enums/CompanyLogoSubparentType";
import { CompanyProfileTabProps } from "hooks/useCompanySettings";
import CompanyAvatar from "components/common/CompanyAvatar";
import BrandLogosPanel, {
  LOGO_ACCEPT,
} from "components/common/branding/BrandLogosPanel";
import type { BrandLogoSlot } from "components/common/branding/BrandLogosPanel";
import { resolveBrandIdentity } from "theme/brandIdentity";
import { useThemePreset } from "theme/themePresetStore";
import BrandPresetDrawer from "./BrandPresetsDrawer";

export const ALL_COLOR_KEYS = [
  ...BRAND_COLOR_KEYS,
  ...BRAND_CHART_KEYS,
  ...BRAND_CARD_KEYS,
];

/** Everything Reset clears outright; the card keys reset to a face instead. */
export const RESETTABLE_COLOR_KEYS = [...BRAND_COLOR_KEYS, ...BRAND_CHART_KEYS];

const deepEqual = (obj1: ThemeColors, obj2: ThemeColors) =>
  ALL_COLOR_KEYS.every(
    (key) => obj1[key as keyof ThemeColors] === obj2[key as keyof ThemeColors],
  );

/** Their OWN overrides only: the platform's would look deliberately picked. */
export const seedFromRecord = (saved: ThemeColors): ThemeColors => {
  const next: ThemeColors = {};
  for (const key of ALL_COLOR_KEYS) {
    const field = key as keyof ThemeColors;
    next[field] = saved[field] || "";
  }
  return next;
};

/**
 * What the user actually changed, against what is SAVED for this company.
 *
 * Diffing against the platform record instead answers a different question,
 * and gets two things wrong: every knob the company never set differs from the
 * platform's, so a save writes "" for all of them; and when the platform's own
 * value is "", clearing a company override produces no diff at all, so the
 * override survives the server's `{...existing, ...incoming}` merge.
 *
 * Exported for the test, because the failure is invisible from the outside:
 * the form goes clean and the value comes back on reload.
 */
export const changedColors = (
  proposed: Partial<ThemeColors>,
  saved: Partial<ThemeColors>,
): ThemeColors => {
  const changed: ThemeColors = {};
  (Object.keys(proposed) as (keyof ThemeColors)[]).forEach((key) => {
    if (proposed[key] !== (saved[key] ?? "")) changed[key] = proposed[key];
  });
  return changed;
};

const FACE_OPTIONS: { face: CardFace; label: string }[] = [
  { face: "stock", label: "Theme default" },
  { face: "brand", label: "Primary color" },
  { face: "background", label: "Background color" },
];

interface ColorSettingsProps {
  customColors: ThemeColors;
  /** Resolves false when the mutation failed, so a caller can roll back. */
  handleUpdateCustomColors: (colors: ThemeColors) => Promise<boolean>;
  uploading: boolean;
  logos?: {
    letterheadLogoAssetUrl: string;
    lightLetterheadLogoAssetUrl: string;
    iconLogoAssetUrl: string;
    cardImageToDisplayUrl: string;
  };
  /** Resolves false when the file was refused or the upload failed. */
  uploadLogo: (
    file: File,
    subParentType: CompanyLogoSubparentType,
  ) => Promise<boolean>;
  /** Resolves false when the logo could not be deleted and is still stored. */
  handleRemoveLogo: (
    subParentType: CompanyLogoSubparentType,
  ) => Promise<boolean>;
  companyDetails: CompanyProfileTabProps;
  showBrandPresets?: boolean;
  openBrandPresetsDrawer: boolean;
  setOpenBrandPresetsDrawer: (open: boolean) => void;
}

const BrandingSettings: React.FC<ColorSettingsProps> = ({
  customColors,
  handleUpdateCustomColors,
  uploading,
  logos,
  uploadLogo,
  handleRemoveLogo,
  companyDetails,
  showBrandPresets,
  openBrandPresetsDrawer,
  setOpenBrandPresetsDrawer,
}) => {
  const { t } = useTranslation();
  const breadcrumbs = useNavBreadcrumbs(t("Branding"));
  // The RESOLVED logos, own over platform over the product default. Right for
  // the preview, which answers "what will the app render", and wrong for the
  // slot state, which asks "has a layer set one" and takes `platform.logos`.
  const {
    platform,
    logos: {
      logo: resolvedLogo,
      lightLogo: resolvedLightLogo,
      platformCardImage,
    },
  } = useBrandingContext();

  // The platform record is the baseline a company inherits. With none, every
  // knob is simply unset and the package supplies it.
  const originalColors: Partial<ThemeColors> = useMemo(
    () => platform.brandColors || {},
    [platform.brandColors],
  );

  const [currentColors, setCurrentColors] = useState<ThemeColors>(() =>
    seedFromRecord(customColors),
  );

  const [proposedColors, setProposedColors] =
    useState<ThemeColors>(currentColors);

  // The record lands after this mounts, so the seed above captured an empty one.
  // A staged edit is left alone: the draft outranks a late read.
  useEffect(() => {
    const seeded = seedFromRecord(customColors);
    if (deepEqual(seeded, currentColors)) return;
    const pristine = deepEqual(proposedColors, currentColors);
    setCurrentColors(seeded);
    if (pristine) setProposedColors(seeded);
  }, [customColors, currentColors, proposedColors]);

  const themePreset = useThemePreset();
  const [celebrating, setCelebrating] = useState(false);

  // The card face never repaints with the scheme, so either scheme's fills serve.
  const draftTokens = useDraftTokens(
    proposedColors,
    originalColors,
    "dark",
    themePreset,
  );
  const fills = cardFills(draftTokens);

  // Which slots are mid-upload. The hook reports one boolean across all four
  // assets and a colour save besides, so it cannot name a slot on its own, and
  // a single key cannot hold two at once.
  const [pendingLogoKeys, setPendingLogoKeys] = useState<string[]>([]);
  // The card artwork's own, since `uploading` also covers a colour save.
  const [cardUploading, setCardUploading] = useState(false);

  const setCardKey = (key: keyof ThemeColors, value: string) =>
    setProposedColors((prev) => ({ ...prev, [key]: value }));

  const hasOwnArt = Boolean(logos?.cardImageToDisplayUrl);
  // What the card wears, own over platform: the radio, the switch and the preview all show it.
  const {
    card: worn,
    face: draftFace,
    faceIsChosen,
    artMode,
    shownFace,
    faceInherited,
    textInherited,
    isLightText,
  } = effectiveCard(proposedColors, originalColors, hasOwnArt);

  // Over an image the ink is a choice, since an image cannot be judged here.
  const inkIsAutomatic = faceIsChosen
    ? draftFace !== "stock" && !hasOwnArt
    : artMode === "none" && Boolean(worn.paymentCardBackground);

  const isResetState =
    RESETTABLE_COLOR_KEYS.every(
      (key) => !proposedColors[key as keyof ThemeColors],
    ) &&
    (isCardFace(originalColors.paymentCardFace)
      ? !proposedColors.paymentCardFace
      : shownFace === "stock") &&
    (originalColors.paymentCardTextMode
      ? !proposedColors.paymentCardTextMode
      : isLightText);

  // Defaults for every tab, staged like any other edit: logos and the card
  // artwork keep their own Remove, so nothing here deletes an upload.
  const handleReset = () => {
    setProposedColors((prev) => ({
      ...prev,
      ...Object.fromEntries(RESETTABLE_COLOR_KEYS.map((key) => [key, ""])),
      ...cardResetPatch(originalColors, shownFace, faceIsChosen),
    }));
  };

  const colorsAreDirty = !deepEqual(proposedColors, currentColors);

  const workspaceName = companyDetails?.businessDBA?.trim();

  const handleSave = async () => {
    const changed = changedColors(proposedColors, currentColors);
    if (Object.keys(changed).length > 0) {
      const stored = await handleUpdateCustomColors(changed);
      if (!stored) return;
    }
    toast.success(
      workspaceName
        ? t("BrandingSavedNewLook", { name: workspaceName })
        : t("BrandingSavedNewLookGeneric"),
    );
    setCelebrating(true);
  };

  const celebrationColors = useMemo(() => {
    const identity =
      resolveBrandIdentity(proposedColors, originalColors) ??
      BRAND_KEYS[DEFAULT_BRAND];
    return [identity.primary, identity.secondary, identity.tertiary];
  }, [proposedColors, originalColors]);

  const logoSlots: BrandLogoSlot[] = [
    {
      key: CompanyLogoSubparentType.LETTERHEAD_LOGO,
      label: "Light logo",
      helper: "LogoSizeHint",
      accept: LOGO_ACCEPT,
      surface: "light",
    },
    {
      key: CompanyLogoSubparentType.LIGHT_LETTERHEAD_LOGO,
      label: "Dark logo",
      helper: "LogoSizeHint",
      accept: LOGO_ACCEPT,
      surface: "dark",
    },
    {
      key: CompanyLogoSubparentType.ICON_LOGO,
      label: "Logo icon",
      helper: "FaviconSizeHint",
      accept: LOGO_ACCEPT,
      // Initials or the Brandfetch mark, which is what the app falls back to.
      fallback: (
        <CompanyAvatar
          name={companyDetails?.businessDBA}
          website={companyDetails?.websiteURL}
          size="xl"
        />
      ),
    },
  ];

  // A removal is as in-flight as an upload, so it marks the slot busy the same
  // way. Without it the DELETE ran with no spinner and with Remove still live.
  const whileSlotBusy = async (
    key: string,
    run: () => Promise<unknown> | void,
  ) => {
    setPendingLogoKeys((keys) => (keys.includes(key) ? keys : [...keys, key]));
    try {
      await run();
    } finally {
      setPendingLogoKeys((keys) => keys.filter((k) => k !== key));
    }
  };

  const handleUploadLogo = (key: string, file: File) =>
    whileSlotBusy(key, () => uploadLogo(file, key as CompanyLogoSubparentType));

  const handleRemoveLogoSlot = (key: string) =>
    whileSlotBusy(key, () => handleRemoveLogo(key as CompanyLogoSubparentType));

  const logosSection = (
    <BrandLogosPanel
      slots={logoSlots}
      currentUrls={{
        [CompanyLogoSubparentType.LETTERHEAD_LOGO]:
          logos?.letterheadLogoAssetUrl,
        [CompanyLogoSubparentType.LIGHT_LETTERHEAD_LOGO]:
          logos?.lightLetterheadLogoAssetUrl,
        [CompanyLogoSubparentType.ICON_LOGO]: logos?.iconLogoAssetUrl,
      }}
      // The PLATFORM's record, not the resolved runtime logo: that one falls
      // back to the bundled Outpave mark, so every slot looked filled and the
      // upload dropzone could never appear. Inheriting a real platform logo
      // still previews it; inheriting the product default is not inheriting.
      fallbackUrls={{
        [CompanyLogoSubparentType.LETTERHEAD_LOGO]:
          platform?.logos?.logo || undefined,
        [CompanyLogoSubparentType.LIGHT_LETTERHEAD_LOGO]:
          platform?.logos?.lightLogo || undefined,
      }}
      uploadingKeys={pendingLogoKeys}
      onUpload={handleUploadLogo}
      onRemove={handleRemoveLogoSlot}
    />
  );

  // Uploading is a choice as much as the face is, so an older record starts
  // wearing it; a chosen face wears any upload anyway.
  const handleUploadCardImage = async (file?: File) => {
    if (!file) return;
    setCardUploading(true);
    try {
      // Only once it landed: a refused or failed upload left "own" staged with nothing to show.
      if (await uploadLogo(file, CompanyLogoSubparentType.CARD_IMAGE)) {
        setCardKey("paymentCardArt", "own");
      }
    } finally {
      setCardUploading(false);
    }
  };

  // The asset goes immediately, so the mode has to as well: staged, a reload
  // between the two left the record saying "own" with nothing to show.
  const handleRemoveCardImage = async () => {
    // What the card wears now, which an upload may have staged without a save, and what the record holds.
    const shownArt = proposedColors.paymentCardArt ?? "";
    const savedArt = currentColors.paymentCardArt ?? "";
    const setArt = (draft: string, saved: string) => {
      setCardKey("paymentCardArt", draft);
      setCurrentColors((prev) => ({ ...prev, paymentCardArt: saved }));
    };

    // Deleting first would leave the record saying "own" with nothing to show.
    setArt("default", "default");
    setCardUploading(true);
    try {
      const stored = await handleUpdateCustomColors({
        ...customColors,
        paymentCardArt: "default",
      });
      if (!stored) {
        setArt(shownArt, savedArt);
        return;
      }
      if (await handleRemoveLogo(CompanyLogoSubparentType.CARD_IMAGE)) return;
      // The artwork is still stored, so the card goes back to wearing it. If
      // that save fails too, the mode stays staged against the saved
      // "default", so the page is dirty and Save retries it.
      const restored = await handleUpdateCustomColors({
        ...customColors,
        paymentCardArt: shownArt,
      });
      setArt(shownArt, restored ? shownArt : "default");
    } finally {
      setCardUploading(false);
    }
  };

  const swatchFor = (face: CardFace) =>
    face === "stock"
      ? {
          backgroundImage: platformDesign(platformCardImage),
          backgroundSize: "cover",
          backgroundPosition: "center",
        }
      : { backgroundColor: fills[face] };

  const cardSection = (
    <CardDesignEditor
      title={t("Card design")}
      help={t("CardDesignHelp")}
      preview={{
        face: draftFace as CardFace | "",
        fills,
        art: faceIsChosen ? (hasOwnArt ? "own" : "none") : artMode,
        textMode: worn.paymentCardTextMode,
        backgroundColor: worn.paymentCardBackground || undefined,
        // The settings hook, not the context: fresher right after an upload.
        artwork: logos?.cardImageToDisplayUrl || undefined,
      }}
      faceOptions={FACE_OPTIONS.map(({ face, label }) => ({
        face,
        label,
        swatch: swatchFor(face),
      }))}
      shownFace={shownFace}
      faceInherited={faceInherited}
      onFaceChange={(face) => setCardKey("paymentCardFace", face)}
      inkIsAutomatic={inkIsAutomatic}
      isLightText={isLightText}
      textInherited={textInherited}
      onTextModeChange={(mode) => setCardKey("paymentCardTextMode", mode)}
      artworkTitle={t("Custom artwork")}
      artworkHelp={t("CustomArtworkHelp")}
      hasArtwork={hasOwnArt}
      uploading={cardUploading}
      onUpload={handleUploadCardImage}
      onRemove={handleRemoveCardImage}
    />
  );

  return (
    <BrandingPage
      breadcrumbs={breadcrumbs}
      title={t("Branding")}
      subtitle={
        workspaceName
          ? t("BrandingSubtitle", { name: workspaceName })
          : t("BrandingSubtitleGeneric")
      }
      secondaryAction={
        <Inline gap="var(--space-3)" wrap={false}>
          {showBrandPresets && (
            <Button
              variant="default-outlined"
              onClick={() => setOpenBrandPresetsDrawer(true)}
            >
              {t("Brand Presets")}
            </Button>
          )}
          <ResetBrandingButton
            onReset={handleReset}
            disabled={isResetState}
            message={t("ResetBrandingCompany")}
          />
          {/* Back to the last save, where Reset goes to the defaults. */}
          <Button
            variant="default-outlined"
            onClick={() => setProposedColors(currentColors)}
            disabled={!colorsAreDirty}
          >
            {t("Discard changes")}
          </Button>
        </Inline>
      }
      primaryAction={
        <Button
          variant="primary-filled"
          onClick={handleSave}
          disabled={!colorsAreDirty}
        >
          {t("Save changes")}
        </Button>
      }
      logos={logosSection}
      colors={proposedColors}
      inherited={originalColors}
      inheritedLabel={t("Platform")}
      onColorChange={(key, hex) =>
        setProposedColors((prev) => ({ ...prev, [key]: hex }))
      }
      chart={chartChoiceOf(proposedColors, originalColors)}
      onChartChange={(next) =>
        setProposedColors((prev) => ({
          ...prev,
          ...chartKeysOf(next, chartChoiceOf(prev, originalColors)),
        }))
      }
      extraTabs={[
        { id: "card", label: "Card", fullWidth: true, content: cardSection },
      ]}
      previewLogoUrl={resolvedLogo || undefined}
      previewLightLogoUrl={resolvedLightLogo || undefined}
    >
      <BrandPresetDrawer
        open={openBrandPresetsDrawer}
        handleCloseBrandPresetDrawer={() => setOpenBrandPresetsDrawer(false)}
        onApplyScheme={(scheme) =>
          setProposedColors((prev) => ({
            ...scheme,
            ...Object.fromEntries(
              BRAND_CARD_KEYS.map((key) => [
                key,
                prev[key as keyof ThemeColors],
              ]),
            ),
          }))
        }
        currentColors={proposedColors}
      />

      <Celebration
        active={celebrating}
        onDone={() => setCelebrating(false)}
        colors={celebrationColors}
      />

      <LoadingWithoutModal open={uploading} />
    </BrandingPage>
  );
};

export default BrandingSettings;
